import type { StatusCheckResponse } from '../types'
import { marked } from '../utils/marked'

export default function StatusCheckModal({
  open,
  onClose,
  data,
  error,
  showSaveButton,
  onSave,
  saving,
  saved,
}: {
  open: boolean
  onClose: () => void
  data: StatusCheckResponse | null
  error?: string | null
  showSaveButton?: boolean
  onSave?: () => void
  saving?: boolean
  saved?: boolean
}) {
  if (!open) return null

  const header = (() => {
    const dt = data?.now ? new Date(data.now) : null
    const asOf = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : ''
    if (data?.mode === 'outcome') return `Outcome check as of ${asOf || 'now'}`
    return `News summary as of ${asOf || 'now'}`
  })()

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-lg sm:text-xl font-semibold text-ink">{header}</h2>
          <button className="modal-close" type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {showSaveButton && data?.mode === 'news' && (
            <div className="flex items-center justify-end gap-2 mb-3">
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm font-semibold border border-rule bg-paper shadow-delphi hover:bg-white/30 transition disabled:opacity-60"
                onClick={onSave}
                disabled={!onSave || saving || saved}
                title="Save this news summary to the database so you can reopen it later."
              >
                {saved ? 'Saved' : saving ? 'Saving…' : 'Save news summary'}
              </button>
            </div>
          )}

          {error && (
            <div className="rounded-delphi border border-wine/30 bg-wine/10 p-3 text-sm text-wine">{error}</div>
          )}

          {data && (
            <>
              {data.mode === 'outcome' && data.inferred_outcome && (
                <div className="rounded-delphi border border-rule bg-paper p-3">
                  <div className="text-xs text-muted tracking-wider font-semibold">AI‑INFERRED OUTCOME</div>
                  <div className="mt-2 text-2xl text-ink font-semibold">
                    {data.inferred_outcome}
                    {data.confidence ? (
                      <span className="ml-2 text-xs text-muted">({data.confidence.toUpperCase()})</span>
                    ) : null}
                  </div>
                  {data.explanation && <div className="mt-2 text-sm text-muted">{data.explanation}</div>}
                </div>
              )}

              <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
                <div className="font-semibold text-ink">Summary</div>
                <div className="mt-2 text-sm text-ink/90 leading-relaxed">{data.headline_summary}</div>
                {data.bullets?.length > 0 && (
                  <ul className="mt-3 list-disc pl-5 text-sm text-muted space-y-1">
                    {data.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>

              {data.links?.length > 0 && (
                <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
                  <div className="font-semibold text-ink">Sources</div>
                  <div
                    className="mt-2 text-sm text-ink/90 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(
                        data.links
                          .slice(0, 4)
                          .map((l) => `- [${l.title || l.url}](${l.url})${l.source ? ` (${l.source})` : ''}`)
                          .join('\n')
                      ),
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}


